/**
 * Pull ranked access blockers from GraphQL mutation errors.
 * Prefers structured `extensions.blockers`; falls back to legacy `email_unverified`.
 */
import { normalizeAccessBlockersFromExtensions } from '@/access/normalizeAccessBlockers';
import type { AccessBlocker } from '@/access/types';
import { EMAIL_UNVERIFIED_REASON, VERIFY_EMAIL_ACTION } from '@/access/types';

function graphQLErrorRemediation(remediation: unknown): { action: string; context: Record<string, unknown> } {
  if (typeof remediation === 'string') {
    return { action: remediation, context: {} };
  }

  if (remediation && typeof remediation === 'object') {
    const payload = remediation as { action?: string; context?: Record<string, unknown> };
    return {
      action: payload.action ?? '',
      context: payload.context ?? {},
    };
  }

  return { action: '', context: {} };
}

export function extractAccessBlockersFromError(error: unknown): AccessBlocker[] {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: Record<string, unknown>; message?: string }> })
    ?.graphQLErrors;

  for (const graphQLError of graphQLErrors ?? []) {
    const blockers = normalizeAccessBlockersFromExtensions(
      graphQLError.extensions?.blockers as Array<Record<string, unknown>> | undefined
    );
    if (blockers.length) {
      return blockers;
    }
  }

  const unverifiedError = graphQLErrors?.find((graphQLError) => graphQLError.extensions?.error_code === EMAIL_UNVERIFIED_REASON);
  if (unverifiedError) {
    const remediation = graphQLErrorRemediation(unverifiedError.extensions?.remediation);
    return normalizeAccessBlockersFromExtensions([
      {
        reason_code: EMAIL_UNVERIFIED_REASON,
        message: unverifiedError.message ?? 'Verify your email before continuing.',
        remediation: remediation.action
          ? remediation
          : { action: VERIFY_EMAIL_ACTION, context: { action: 'apply_to_job', moment: 'parent_interaction' } },
      },
    ]);
  }

  return [];
}
